import { createElement, useState } from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@/components/ui';
import {
  formatAllDebugTurnsForCopy,
  formatPromptPreviewForCopy,
  formatTurnDebugForCopy,
  turnDebugPreview,
  type ChatPromptPreview,
  type ChatTurnDebugRound,
  type ChatTurnDebugTrace,
  type CompletionMessage,
} from '@/lib/chat/turn-debug';

type ChatDebugPaneProps = {
  turns: ChatTurnDebugTrace[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onLoadPrompt: () => Promise<ChatPromptPreview>;
};

function copyText(text: string) {
  void navigator.clipboard.writeText(text);
}

function MessageBlock({ message, index }: { message: CompletionMessage; index: number }) {
  const roleClass = `chat-debug-role chat-debug-role-${message.role}`;
  let body = '';

  if (message.role === 'system' || message.role === 'user') {
    body = message.content;
  } else if (message.role === 'assistant') {
    const parts: string[] = [];
    if (message.content) {
      parts.push(message.content);
    }
    if (message.tool_calls && message.tool_calls.length > 0) {
      parts.push(JSON.stringify({ tool_calls: message.tool_calls }, null, 2));
    }
    body = parts.join('\n\n');
  } else {
    body = `(tool_call_id: ${message.tool_call_id})\n${message.content}`;
  }

  return createElement(
    'div',
    { className: 'chat-debug-message' },
    createElement(
      'div',
      { className: roleClass },
      `#${index + 1} ${message.role}`,
    ),
    createElement('pre', { className: 'chat-debug-pre' }, body || '(empty)'),
  );
}

function MessagesSection({ title, messages }: { title: string; messages: CompletionMessage[] }) {
  return createElement(
    'div',
    { className: 'chat-debug-section' },
    createElement('div', { className: 'chat-debug-section-title' }, title),
    messages.length === 0
      ? createElement('pre', { className: 'chat-debug-pre chat-debug-pre-muted' }, '(empty)')
      : messages.map((message, index) =>
          createElement(MessageBlock, { key: `${title}-${index}`, message, index }),
        ),
  );
}

function RoundBlock({ round }: { round: ChatTurnDebugRound }) {
  const [open, setOpen] = useState(false);
  const toolCount = round.toolExecutions.length;
  const label = `Round ${round.round} (${round.kind}) · ${round.toolNames.length} tool${round.toolNames.length === 1 ? '' : 's'} offered · ${toolCount} execution${toolCount === 1 ? '' : 's'}`;

  return createElement(
    'div',
    { className: 'chat-debug-round' },
    createElement(
      'button',
      {
        type: 'button',
        className: 'chat-debug-round-toggle',
        onClick: () => setOpen((value) => !value),
      },
      createElement('span', { className: 'chat-debug-round-chevron', 'aria-hidden': true }, open ? '▾' : '▸'),
      createElement('span', null, label),
    ),
    open
      ? createElement(
          'div',
          { className: 'chat-debug-round-body' },
          createElement(
            'div',
            { className: 'chat-debug-meta' },
            `model: ${round.response.model || 'n/a'} · finish: ${round.response.finishReason ?? 'n/a'}`,
          ),
          createElement(
            'div',
            { className: 'chat-debug-section' },
            createElement('div', { className: 'chat-debug-section-title' }, 'Model response'),
            createElement(
              'pre',
              { className: 'chat-debug-pre' },
              round.response.content ?? '(null)',
            ),
            round.response.toolCalls.length > 0
              ? createElement(
                  'pre',
                  { className: 'chat-debug-pre' },
                  JSON.stringify({ tool_calls: round.response.toolCalls }, null, 2),
                )
              : null,
          ),
          toolCount > 0
            ? createElement(
                'div',
                { className: 'chat-debug-section' },
                createElement('div', { className: 'chat-debug-section-title' }, 'Tool executions'),
                round.toolExecutions.map((exec, index) =>
                  createElement(
                    'pre',
                    {
                      key: `${exec.name}-${index}`,
                      className: 'chat-debug-pre',
                    },
                    [
                      `${exec.name}${exec.targetTitle ? ` ("${exec.targetTitle}")` : ''}`,
                      `ok: ${exec.ok}`,
                      `summary: ${exec.summary}`,
                      `args: ${JSON.stringify(exec.args, null, 2)}`,
                      `result: ${exec.result}`,
                    ].join('\n'),
                  ),
                ),
              )
            : null,
        )
      : null,
  );
}

function TurnBlock({
  trace,
  turnNumber,
  isLatest,
}: {
  trace: ChatTurnDebugTrace;
  turnNumber: number;
  isLatest: boolean;
}) {
  const [open, setOpen] = useState(isLatest);
  const [copied, setCopied] = useState(false);

  function onCopy() {
    copyText(formatTurnDebugForCopy(trace));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return createElement(
    'div',
    { className: 'chat-debug-turn' },
    createElement(
      'div',
      { className: 'chat-debug-turn-header' },
      createElement(
        'button',
        {
          type: 'button',
          className: 'chat-debug-turn-toggle',
          onClick: () => setOpen((value) => !value),
        },
        createElement('span', { className: 'chat-debug-round-chevron', 'aria-hidden': true }, open ? '▾' : '▸'),
        createElement('span', { className: 'chat-debug-turn-title' }, `Turn ${turnNumber}`),
        createElement('span', { className: 'chat-debug-turn-preview' }, turnDebugPreview(trace)),
      ),
      createElement(
        'button',
        {
          type: 'button',
          className: 'chat-debug-copy-btn',
          onClick: onCopy,
        },
        copied ? 'Copied' : 'Copy',
      ),
    ),
    open
      ? createElement(
          'div',
          { className: 'chat-debug-turn-body' },
          createElement(
            'div',
            { className: 'chat-debug-meta' },
            `source: ${trace.source} · model: ${trace.model || 'n/a'} · context tokens (est.): ${trace.contextTokens} · usage: ${JSON.stringify(trace.usage)}`,
          ),
          trace.source === 'reconstructed'
            ? createElement(
                'div',
                { className: 'chat-debug-meta chat-debug-pre-muted' },
                'Reconstructed from saved messages — system prompt not captured. Use "Load current prompt" above to see the exact system prompt with pinned context.',
              )
            : null,
          createElement(
            'div',
            { className: 'chat-debug-section' },
            createElement('div', { className: 'chat-debug-section-title' }, 'User message'),
            createElement('pre', { className: 'chat-debug-pre' }, trace.userText),
          ),
          createElement(
            'div',
            { className: 'chat-debug-section' },
            createElement('div', { className: 'chat-debug-section-title' }, 'Tools offered'),
            createElement(
              'pre',
              { className: 'chat-debug-pre' },
              trace.tools.length > 0
                ? trace.tools.map((tool) => `- ${tool.name}: ${tool.description}`).join('\n')
                : '(none)',
            ),
          ),
          createElement(MessagesSection, {
            title: 'Initial prompt messages',
            messages: trace.initialMessages,
          }),
          trace.rounds.map((round) => createElement(RoundBlock, { key: round.round, round })),
          createElement(MessagesSection, {
            title: 'Final messages sent to model',
            messages: trace.finalMessages,
          }),
          createElement(
            'div',
            { className: 'chat-debug-section' },
            createElement('div', { className: 'chat-debug-section-title' }, 'Assistant reply'),
            createElement('pre', { className: 'chat-debug-pre' }, trace.assistantText || '(empty)'),
          ),
        )
      : null,
  );
}

function PromptPreviewSection({ onLoadPrompt }: { onLoadPrompt: () => Promise<ChatPromptPreview> }) {
  const [preview, setPreview] = useState<ChatPromptPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await onLoadPrompt();
      setPreview(result);
      setOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompt');
    } finally {
      setLoading(false);
    }
  }

  function onCopy() {
    if (!preview) {
      return;
    }
    copyText(formatPromptPreviewForCopy(preview));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return createElement(
    'div',
    { className: 'chat-debug-turn' },
    createElement(
      'div',
      { className: 'chat-debug-turn-header' },
      createElement(
        'button',
        {
          type: 'button',
          className: 'chat-debug-turn-toggle',
          onClick: () => (preview ? setOpen((value) => !value) : void load()),
        },
        createElement('span', { className: 'chat-debug-round-chevron', 'aria-hidden': true }, open ? '▾' : '▸'),
        createElement('span', { className: 'chat-debug-turn-title' }, 'Current prompt'),
        createElement(
          'span',
          { className: 'chat-debug-turn-preview' },
          preview
            ? `${preview.messages.length} messages · ${preview.tools.length} tools · ~${preview.contextTokens} tokens`
            : 'Exact system prompt + pinned context that will be sent next',
        ),
      ),
      createElement(
        'button',
        {
          type: 'button',
          className: 'chat-debug-copy-btn',
          onClick: () => (preview ? onCopy() : void load()),
        },
        loading ? 'Loading…' : preview ? (copied ? 'Copied' : 'Copy') : 'Load',
      ),
    ),
    open && preview
      ? createElement(
          'div',
          { className: 'chat-debug-turn-body' },
          createElement(
            'div',
            { className: 'chat-debug-meta' },
            `model: ${preview.model} · context tokens (est.): ${preview.contextTokens}`,
          ),
          createElement(
            'div',
            { className: 'chat-debug-section' },
            createElement('div', { className: 'chat-debug-section-title' }, 'Tools offered'),
            createElement(
              'pre',
              { className: 'chat-debug-pre' },
              preview.tools.length > 0
                ? preview.tools.map((tool) => `- ${tool.name}: ${tool.description}`).join('\n')
                : '(none)',
            ),
          ),
          createElement(MessagesSection, {
            title: 'Prompt messages (incl. system prompt)',
            messages: preview.messages,
          }),
        )
      : null,
    error
      ? createElement('pre', { className: 'chat-debug-pre chat-debug-pre-muted' }, error)
      : null,
  );
}

function CopyAllDebugButton({ turns }: { turns: ChatTurnDebugTrace[] }) {
  const [copied, setCopied] = useState(false);

  function onCopy() {
    copyText(formatAllDebugTurnsForCopy(turns));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  if (turns.length === 0) {
    return null;
  }

  return createElement(
    'button',
    {
      type: 'button',
      className: 'chat-debug-copy-all-btn',
      onClick: (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        onCopy();
      },
    },
    copied ? 'Copied all' : 'Copy all',
  );
}

export function ChatDebugPane({ turns, expanded, onToggleExpanded, onLoadPrompt }: ChatDebugPaneProps) {
  return (
    <View className={`chat-debug-pane${expanded ? ' chat-debug-pane-open' : ''}`}>
      <Pressable
        className="chat-debug-pane-header flex-row items-center gap-1"
        onPress={onToggleExpanded}
      >
        <Text variant="caption" className="shrink-0 font-medium">
          Debug · {turns.length} {turns.length === 1 ? 'turn' : 'turns'}
        </Text>
        <Text variant="caption" className="shrink-0 text-muted">
          {expanded ? '▾' : '▸'}
        </Text>
        <Text variant="caption" className="min-w-0 flex-1 truncate text-muted">
          Full prompt/response context per turn
        </Text>
        {createElement(CopyAllDebugButton, { turns })}
      </Pressable>

      {expanded ? (
        <View className="chat-debug-pane-body">
          {createElement(PromptPreviewSection, { onLoadPrompt })}
          {turns.length === 0 ? (
            <Text variant="caption" className="text-muted">
              No turns recorded yet. Send a message, or use “Current prompt” above to inspect what
              will be sent.
            </Text>
          ) : (
            turns.map((trace, index) =>
              createElement(TurnBlock, {
                key: trace.assistantMessageId ?? `${trace.createdAt}-${index}`,
                trace,
                turnNumber: index + 1,
                isLatest: index === turns.length - 1,
              }),
            )
          )}
        </View>
      ) : null}
    </View>
  );
}
